import React from 'react';
import cn from '@/utils/cn';
import { Finding } from './contentAudit.types';

// Theme-aware forks of admin/ContentAudit/findingColors — the pill hexes there assume a light
// background. Action is the editor's primary skim signal; severity is the demoted dot beneath it.
const ACTION_CLASS: Record<string, string> = {
  'Edit candidate': 'tw-text-spa-warning',
  'Leave & defend': 'tw-text-spa-success',
  'Must edit': 'tw-text-spa-error',
  'Note only': 'tw-text-spa-secondary',
};

const SEVERITY_DOT_CLASS: Record<string, string> = {
  High: 'tw-bg-spa-error',
  Low: 'tw-bg-spa-tertiary',
  Med: 'tw-bg-spa-warning',
};

interface Props {
  finding: Finding;
  showSection?: boolean;
}

// The model sometimes restates the quote in `location` ("Second paragraph: '<full quote>'").
// When the part after the colon just echoes the flagged text, keep only the positional prefix.
const trimLocation = (location: string, quote: string): string => {
  const colon = location.indexOf(':');
  if (colon > -1 && quote) {
    const after = location.slice(colon + 1).replace(/['"“”]/g, '').trim();
    if (after.includes(quote.slice(0, 25))) return location.slice(0, colon).trim();
  }
  return location;
};

// SPA fork of admin/ContentAudit/FindingNote — same two-column ledger, tw-spa-* tokens.
const SPAFindingNote = ({ finding, showSection = false }: Props) => {
  const showStandards = Boolean(finding.standards_driven) && finding.standards_driven.toLowerCase() !== 'no';
  const location = finding.location ? trimLocation(finding.location, finding.flagged_text) : '';
  const where = finding.is_asset
    ? finding.section_title
    : [showSection ? finding.section_title : null, location].filter(Boolean).join(' · ');
  const meta = [
    finding.category,
    finding.lens || null,
    showStandards ? `Standards: ${finding.standards_driven}` : null,
    finding.notes || null,
  ].filter(Boolean).join(' · ');

  return (
    <div className="tw-flex tw-gap-4">
      <div className="tw-w-28 tw-shrink-0">
        <span
          className={cn(
            'tw-text-sm tw-font-bold tw-leading-5',
            ACTION_CLASS[finding.action] || ACTION_CLASS['Note only'],
          )}
        >
          {finding.action}
        </span>
        <div className="tw-mt-1 tw-flex tw-items-center tw-gap-1.5">
          <span
            className={cn(
              'tw-h-2 tw-w-2 tw-shrink-0 tw-rounded-full',
              SEVERITY_DOT_CLASS[finding.severity] || SEVERITY_DOT_CLASS.Low,
            )}
          />
          <span className="tw-text-xs tw-tracking-wide tw-text-spa-secondary">
            {finding.severity.toUpperCase()}
          </span>
        </div>
      </div>

      <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-2.5">
        <blockquote className="tw-m-0 tw-border-0 tw-border-l-2 tw-border-solid tw-border-spa-border-hover tw-pl-4">
          <p className="tw-m-0 tw-text-base tw-font-medium tw-leading-7 tw-text-spa-on-bg">
            {finding.flagged_text}
          </p>
          {where && (
            <p className="tw-m-0 tw-mt-1 tw-text-xs tw-leading-5 tw-text-spa-secondary">— {where}</p>
          )}
        </blockquote>
        {finding.suggestion && (
          <p className="tw-m-0 tw-text-sm tw-leading-6 tw-text-spa-primary">{finding.suggestion}</p>
        )}
        {meta && <p className="tw-m-0 tw-text-xs tw-leading-5 tw-text-spa-secondary">{meta}</p>}
      </div>
    </div>
  );
};

export default SPAFindingNote;
